import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./Login";
import CoreCRM from "./elyon-core";
import ElyonPayX from "./elyon-pay";
import Marketing from "./elyon-marketing";
import AIAgent from "./elyon-ai-agent";
import ShopManager from "./ShopManager";
import PublicBooking from "./PublicBooking";
import PublicShop from "./PublicShop";
import CardGuarantee from "./CardGuarantee";
import NovaGlowLanding from "./NovaGlowLanding";
import ClientPortal from "./ClientPortal";
import NewLeadAlert from "./NewLeadAlert";
import AIHandoffAlert from "./AIHandoffAlert";
import { ConfirmBookingAlert } from "./ConfirmQueue";
import LiveCallPanel from "./LiveCallPanel";
import SophiaLiveBadge from "./SophiaLiveBadge";
import { useIsMobile } from "./useIsMobile";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", border:"#e8e4df",
  gold:"#d4af37", goldBg:"rgba(212,175,55,0.12)",
  borderGold:"rgba(212,175,55,0.35)",
  text:"#1a1a2e", muted:"#9ca3af",
  sidebar:"#0f0f1e",
};
const F = "'Montserrat','Plus Jakarta Sans',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const MODULES = [
  {id:"core", label:"CRM", emoji:"🏠"},
  // Elyon Pay removed at owner request — it lived here but wasn't used.
  // Add back when needed: {id:"pay", label:"Elyon Pay", emoji:"💳"},
  {id:"marketing", label:"Marketing", emoji:"📣"},
  {id:"shop", label:"Shop", emoji:"🛍️"},
  {id:"ai", label:"AI Agent", emoji:"🤖"},
];

function AppContent() {
  const { user, profile, loading, signOut, canAccessTopModule } = useAuth();
  const [mod, setMod] = useState("core");
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  // The live-call panel's "Book appointment" jumps to the CRM from any module.
  useEffect(() => {
    const go = () => setMod("core");
    window.addEventListener("elyon:goto-core", go);
    return () => window.removeEventListener("elyon:goto-core", go);
  }, []);

  if (loading) {
    return (<div style={{minHeight:"100dvh",background:C.sidebar,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F,color:C.gold,fontSize:14}}>Loading...</div>);
  }

  if (!user || !profile) return <Login />;

  const visibleModules = MODULES.filter(m => canAccessTopModule(m.id));
  const currentMod = visibleModules.find(m => m.id === mod) ? mod : (visibleModules[0]?.id || "core");

  return (
    <div style={{minHeight:"100dvh",background:C.bg,fontFamily:F}}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&family=Playfair+Display:wght@500;600;700;800;900&family=Cormorant+Garamond:wght@400;600;700;800&display=swap" rel="stylesheet"/>
      {/* Top bar — compact wordmark + hamburger on mobile, full pill nav on desktop. */}
      <div style={{
        background:"linear-gradient(180deg, #0a0a18 0%, #0f0f1e 100%)",
        padding:isMobile?"10px 14px":"16px 36px",
        display:"flex",
        alignItems:"center",
        gap:isMobile?8:12,
        position:"sticky",top:0,zIndex:300,
        borderBottom:"1px solid rgba(180,140,60,0.35)",
        boxShadow:"0 8px 32px rgba(0,0,0,0.35), 0 1px 0 rgba(180,140,60,0.18) inset",
        paddingTop:`calc(${isMobile?"10px":"16px"} + env(safe-area-inset-top))`,
      }}>
        {isMobile && (
          <button
            onClick={()=>setMenuOpen(v=>!v)}
            aria-label="Menu"
            style={{
              background:"rgba(255,255,255,0.05)",
              border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:10,
              padding:"8px 11px",
              cursor:"pointer",
              color:C.gold,
              fontSize:18,
              lineHeight:1,
              minWidth:44,minHeight:44,
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>{menuOpen?"✕":"☰"}</button>
        )}
        <div style={{display:"flex",alignItems:"baseline",gap:6,marginRight:isMobile?0:28}}>
          <div style={{fontSize:isMobile?18:24,fontWeight:300,color:C.gold,fontFamily:S,letterSpacing:isMobile?"0.22em":"0.32em"}}>ELYON</div>
          {!isMobile && <div style={{width:5,height:5,borderRadius:"50%",background:C.gold,opacity:0.7,marginBottom:4}}/>}
        </div>

        {/* Desktop: pills inline. Mobile: rendered inside the hamburger dropdown below. */}
        {!isMobile && visibleModules.map(m=>{
          const active = currentMod===m.id;
          return (
            <button key={m.id} onClick={()=>setMod(m.id)} style={{
              padding:"9px 18px",
              fontSize:11.5,
              fontWeight:active?800:600,
              color:active?"#0a0a18":"rgba(255,255,255,0.78)",
              background:active?"linear-gradient(135deg, "+C.gold+", #c9a04a)":"transparent",
              border:"1px solid "+(active?C.gold:"rgba(255,255,255,0.08)"),
              borderRadius:99,
              cursor:"pointer",
              fontFamily:F,
              display:"flex",
              alignItems:"center",
              gap:8,
              letterSpacing:"0.14em",
              textTransform:"uppercase",
              boxShadow:active?"0 4px 14px rgba(180,140,60,0.35)":"none",
              transition:"all 0.18s ease",
            }}
            onMouseEnter={e=>{if(!active){e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="rgba(180,140,60,0.35)";}}}
            onMouseLeave={e=>{if(!active){e.currentTarget.style.color="rgba(255,255,255,0.78)";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";}}}>
              <span style={{fontSize:14}}>{m.emoji}</span><span>{m.label}</span>
            </button>
          );
        })}

        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:isMobile?6:10}}>
          {!isMobile && (
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"6px 12px 6px 6px",background:"rgba(255,255,255,0.04)",borderRadius:99,border:"1px solid rgba(255,255,255,0.08)"}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg, "+C.gold+", #8a6a2a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",boxShadow:"0 2px 6px rgba(180,140,60,0.35)"}}>{(profile.full_name||profile.email||"U").charAt(0).toUpperCase()}</div>
              <div style={{lineHeight:1.2}}>
                <div style={{fontSize:11,color:"#fff",fontWeight:600}}>{profile.full_name || profile.email}</div>
                <div style={{fontSize:9,color:C.gold,textTransform:"uppercase",letterSpacing:"0.15em",marginTop:1,fontWeight:600}}>{profile.role}</div>
              </div>
            </div>
          )}
          {isMobile ? (
            <button onClick={signOut} aria-label="Sign out" title="Sign out" style={{
              width:40,height:40,padding:0,
              background:"rgba(255,255,255,0.05)",
              border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:10,
              cursor:"pointer",
              color:"#fff",
              fontSize:14,
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>⏻</button>
          ) : (
            <button onClick={signOut} style={{padding:"8px 14px",fontSize:10.5,fontWeight:600,color:"rgba(255,255,255,0.7)",background:"transparent",border:"1px solid rgba(255,255,255,0.12)",borderRadius:99,cursor:"pointer",fontFamily:F,textTransform:"uppercase",letterSpacing:"0.12em"}}
            onMouseEnter={e=>{e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="rgba(180,140,60,0.4)";}}
            onMouseLeave={e=>{e.currentTarget.style.color="rgba(255,255,255,0.7)";e.currentTarget.style.borderColor="rgba(255,255,255,0.12)";}}>Sign Out</button>
          )}
        </div>
      </div>

      {/* Mobile dropdown menu: tap a module to switch + show user info */}
      {isMobile && menuOpen && (
        <div style={{
          position:"sticky",top:0,zIndex:299,
          background:"#0f0f1e",
          borderBottom:"1px solid rgba(180,140,60,0.35)",
          padding:"12px 14px 16px",
          display:"flex",flexDirection:"column",gap:8,
          animation:"el-fade-in 0.18s ease-out",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"rgba(255,255,255,0.04)",borderRadius:10,marginBottom:6}}>
            <div style={{width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg, "+C.gold+", #8a6a2a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff"}}>{(profile.full_name||profile.email||"U").charAt(0).toUpperCase()}</div>
            <div style={{lineHeight:1.3,flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:"#fff",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{profile.full_name || profile.email}</div>
              <div style={{fontSize:10,color:C.gold,textTransform:"uppercase",letterSpacing:"0.15em",marginTop:1,fontWeight:600}}>{profile.role}</div>
            </div>
          </div>
          {visibleModules.map(m=>{
            const active = currentMod===m.id;
            return (
              <button key={m.id} onClick={()=>{setMod(m.id);setMenuOpen(false);}} style={{
                padding:"14px 16px",
                fontSize:13,
                fontWeight:active?800:600,
                color:active?"#0a0a18":"#fff",
                background:active?"linear-gradient(135deg, "+C.gold+", #c9a04a)":"rgba(255,255,255,0.05)",
                border:"1px solid "+(active?C.gold:"rgba(255,255,255,0.08)"),
                borderRadius:10,
                cursor:"pointer",
                fontFamily:F,
                display:"flex",
                alignItems:"center",
                gap:10,
                letterSpacing:"0.08em",
                textTransform:"uppercase",
                textAlign:"left",
              }}>
                <span style={{fontSize:18}}>{m.emoji}</span><span>{m.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {currentMod==="core" && <CoreCRM />}
      {currentMod==="pay" && <ElyonPayX />}
      {currentMod==="marketing" && <Marketing />}
      {currentMod==="shop" && <div style={{minHeight:"100dvh",background:"#f8f5f0",padding:"26px 28px"}}><div style={{maxWidth:1200,margin:"0 auto"}}><ShopManager /></div></div>}
      {currentMod==="ai" && <AIAgent />}

      {/* Global popup that fires on every new lead — only renders for allowed roles */}
      <NewLeadAlert user={user} profile={profile}/>
      {/* Global popup when the AI bot can't handle a message and escalates to a human */}
      <AIHandoffAlert user={user} profile={profile}/>
      {/* Sophia books; the appointment dies unless someone rings to confirm it.
          46 of 46 unconfirmed AI bookings were cancelled, 0 of 16 confirmed ones. */}
      <ConfirmBookingAlert profile={profile} userName={profile?.full_name || profile?.name || user?.email}/>
      {/* Auto-dialer screen-pop: the lead's details the moment the desk is on the call — for every user */}
      <LiveCallPanel profile={profile}/>
      {/* Owner/admins: a pill while Sophia (AI voice) is on a call — click to listen live */}
      <SophiaLiveBadge profile={profile}/>
    </div>
  );
}

function MainApp() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default function App() {
  // ★ When served from a shop subdomain (skincare. / shop. / store. — e.g.
  //   skincare.novamedicalspa.com), the ENTIRE site IS the storefront: every
  //   path renders the shop, so the bare domain root shows products (not the
  //   CRM login). On elyonhq.com it behaves normally.
  const isShopHost = typeof window !== "undefined" && /^(skincare|shop|store)\./i.test(window.location.hostname || "");

  if (isShopHost) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/book/:slug" element={<PublicBooking />} />
          <Route path="/book" element={<PublicBooking />} />
          <Route path="/me/:token" element={<ClientPortal />} />
          <Route path="/shop" element={<PublicShop />} />
          <Route path="/glow" element={<NovaGlowLanding />} />
          <Route path="/card" element={<CardGuarantee />} />
          <Route path="*" element={<NovaGlowLanding />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/book/:slug" element={<PublicBooking />} />
        <Route path="/book" element={<PublicBooking />} />
        <Route path="/shop" element={<PublicShop />} />
        <Route path="/skincare" element={<PublicShop />} />
        <Route path="/glow" element={<NovaGlowLanding />} />
        <Route path="/me/:token" element={<ClientPortal />} />
        <Route path="/card" element={<CardGuarantee />} />
        <Route path="/" element={<MainApp />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
